import 'react-dropdown/style.css'

import { faSync } from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { Button, Modal } from '@orobix/orobix-fe-components'
import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useMutation, useQueryClient } from 'react-query'
import { toast } from 'react-toastify'

import { post } from '../../utils/api'
import { createSocket } from '../../utils/webSocket'

export const DatasetsListActionsButton = () => {
  const queryClient = useQueryClient()
  const { t } = useTranslation()

  const [syncConfirmModal, setSyncConfirmModal] = useState(false)
  const [trackingSyncDatasetId, setTrackingSyncDatasetId] = useState<
    string | null
  >(null)

  const handleSyncSuccess = (datasetId: string) => {
    queryClient.invalidateQueries(['data-ingestions'])
    setTrackingSyncDatasetId(datasetId)
  }

  const handleSyncConfirm = () => {
    syncData()
    setSyncConfirmModal(false) // Close modal immediately
  }

  // WebSocket listener for completion toasts
  useEffect(() => {
    if (!trackingSyncDatasetId) return undefined

    const websocket = createSocket('/data-ingestion/status')

    websocket.onmessage = (event: MessageEvent<string>) => {
      const data = JSON.parse(event.data)

      if (data.id === Number(trackingSyncDatasetId)) {
        if (data.status === 'INGESTION_DONE') {
          toast.success(t('Data has been successfully synced'))
          setTrackingSyncDatasetId(null)
          websocket.close()
        } else if (data.status === 'INGESTION_FAILED') {
          toast.error(t('Failed to sync data. Please try again.'))
          setTrackingSyncDatasetId(null)
          websocket.close()
        } else if (data.status === 'INGESTION_TIMED_OUT') {
          toast.error(t('Data sync took too long. Please try again.'))
          setTrackingSyncDatasetId(null)
          websocket.close()
        } else if (data.status === 'INGESTION_NOT_VALID') {
          toast.error(
            t('Invalid data from ERP. Please check your data source.')
          )
          setTrackingSyncDatasetId(null)
          websocket.close()
        }
      }
    }

    return () => {
      websocket.close()
    }
  }, [trackingSyncDatasetId, t])

  const { mutate: syncData } = useSyncData(handleSyncSuccess)

  return (
    <>
      <Button
        icon={<FontAwesomeIcon icon={faSync} />}
        onClick={() => setSyncConfirmModal(true)}
        type="action"
        label={t('Sync data from ERP')}
        title={t('Click to get data from ERP')}
        size="md"
      />

      {syncConfirmModal && (
        <Modal
          title={t('Confirm Sync')}
          onCloseRequest={() => setSyncConfirmModal(false)}
          footer={
            <>
              <Button
                buttonType="button"
                label={t('Cancel')}
                onClick={() => setSyncConfirmModal(false)}
              />
              <Button
                label={t('Proceed')}
                type="action"
                onClick={handleSyncConfirm}
              />
            </>
          }
        >
          <p>{t('Are you sure you want to sync data?')}</p>
        </Modal>
      )}
    </>
  )
}

type SyncResponse = {
  task_id: string
  dataset_id: string
  dataset_name: string
  status: string
  status_url: string
}

const useSyncData = (onSuccess: (datasetId: string) => void) => {
  const { t } = useTranslation()

  return useMutation<SyncResponse, unknown, void>(
    ['data-sync-start'],
    () => post<SyncResponse>('/data-sync/', {}),
    {
      retry: false,
      useErrorBoundary: false,
      onSuccess: (data) => {
        toast.info(t('Data syncing'))
        // Immediately refresh the dataset list to show the new syncing dataset
        onSuccess(data.dataset_id)
      },
      onError: (error) => {
        toast.error(t('Failed to sync data. Please try again.'))
        // eslint-disable-next-line no-console
        console.error('Sync start error:', error)
      },
    }
  )
}
